import {
  FREE_SHIPPING_OPTION,
  NO_DISCOUNT_CODE,
  SHIPPING_OPTION_CODE_TO_TITLE_MAP,
  SHORT_NAME_METAFIELD_KEY,
} from "@/domains/careplan/models/constants";
import { env } from "@/env";
import type {
  Cart,
  CartDeliveryOption,
  CartDiscountAllocation,
  GetCartQuery,
} from "@/types/storefront-api.gen";
import { formatAmount, formatAmountAsString, roundToCents } from "@/utils/money";

const getAllocatedAmount = (allocations: CartDiscountAllocation[]) => {
  return allocations.reduce((sum, allocation) => {
    return sum + parseFloat((allocation.discountedAmount?.amount as string) || "0");
  }, 0);
};

const isCareplanAllocation = (allocation: CartDiscountAllocation) => {
  return "code" in allocation && allocation.code === env.VITE_CAREPLAN_DISCOUNT_CODE;
};

const getGroupedDiscountAllocations = (
  allocations: CartDiscountAllocation[],
  cartSubtotalAmount: number,
) => {
  return Object.entries(
    allocations.reduce<Record<string, CartDiscountAllocation[]>>((groups, allocation) => {
      const code = "code" in allocation ? (allocation.code as string) : NO_DISCOUNT_CODE;
      if (!groups[code]) {
        groups[code] = [];
      }
      groups[code].push(allocation);

      return groups;
    }, {}),
  ).map(([code, allocations]) => {
    const total = getAllocatedAmount(allocations);

    const percentage = cartSubtotalAmount > 0 ? Math.round((total / cartSubtotalAmount) * 100) : 0;

    return {
      code,
      displayTotal: formatAmountAsString(total.toString()),
      total,
      percentage,
    };
  });
};

const getSelectedShippingOptionTitle = (cartSelectedDeliveryOption: CartDeliveryOption) => {
  return cartSelectedDeliveryOption.code &&
    SHIPPING_OPTION_CODE_TO_TITLE_MAP[
      cartSelectedDeliveryOption.code as keyof typeof SHIPPING_OPTION_CODE_TO_TITLE_MAP
    ]
    ? SHIPPING_OPTION_CODE_TO_TITLE_MAP[
        cartSelectedDeliveryOption.code as keyof typeof SHIPPING_OPTION_CODE_TO_TITLE_MAP
      ]
    : "Unknown Delivery Option";
};

export const getCartLines = (cart?: Cart) => {
  return (cart?.lines.edges ?? []).map((edge) => {
    const sellingPlanId = edge.node.sellingPlanAllocation?.sellingPlan.id.split("/").at(-1);

    const prices = edge.node.sellingPlanAllocation?.priceAdjustments?.[0];
    const lineSubtotalAmount = Number(edge.node.cost.subtotalAmount?.amount);
    const lineAmountBeforeDiscounts = Number.isFinite(lineSubtotalAmount)
      ? lineSubtotalAmount
      : Number(edge.node.cost.amountPerQuantity.amount) * edge.node.quantity;
    const lineAmountAfterDiscounts = Number(edge.node.cost.totalAmount.amount);
    const promoAmount = getAllocatedAmount(
      edge.node.discountAllocations.filter((allocation) => {
        return !isCareplanAllocation(allocation);
      }),
    );
    const lineAmountAfterCareplanDiscount = lineAmountAfterDiscounts + promoAmount;
    const careplanDiscountAmount = lineAmountBeforeDiscounts - lineAmountAfterCareplanDiscount;

    const name = edge.node.merchandise.product.title;

    const unitListPrice = Number(edge.node.merchandise.price.amount);
    const unitPrice =
      edge.node.quantity > 0
        ? roundToCents(lineAmountAfterCareplanDiscount / edge.node.quantity)
        : unitListPrice;

    const shortName =
      edge.node.merchandise.product.metafields.find((metafield) => {
        return metafield?.key === SHORT_NAME_METAFIELD_KEY;
      })?.value ?? name;

    return {
      id: edge.node.id,
      sku: edge.node.merchandise.sku,
      sellingPlanId: sellingPlanId ? Number(sellingPlanId) : null,
      quantity: edge.node.quantity,
      variantId: edge.node.merchandise.id.split("/").at(-1),
      merchandiseId: edge.node.merchandise.id,
      image: edge.node.merchandise.image?.url,
      name,
      shortName,
      price: Number(edge.node.cost.totalAmount.amount as string),
      originalPriceAsString: formatAmountAsString(edge.node.merchandise.price.amount),
      priceAsString: formatAmountAsString(edge.node.cost.totalAmount.amount),
      discountAmount: prices
        ? Number(prices.compareAtPrice.amount) - Number(prices.price.amount)
        : 0,
      careplanDiscountAmount,
      unitPrice,
      unitPriceAsString: formatAmount(unitPrice),
      unitListPrice,
      unitListPriceAsString: formatAmount(unitListPrice),
    };
  });
};

export const createCareplanCartDataModel = (cartQuery?: GetCartQuery) => {
  const cart = cartQuery?.cart;

  if (!cart) {
    return null;
  }

  const cartSelectedDeliveryOption = cart.deliveryGroups.nodes.at(0)?.selectedDeliveryOption;
  const subtotalAmount = Number(cart.cost.subtotalAmount.amount as string);

  const items = getCartLines(cart as Cart);

  const lineDiscountAllocations = cart.lines.edges.flatMap((edge) => {
    return edge.node.discountAllocations;
  });

  const subscriptionDiscount = items.reduce((total, item) => {
    return total + item.discountAmount * item.quantity;
  }, 0);

  const careplanDiscount = items.reduce((total, item) => {
    return total + item.careplanDiscountAmount;
  }, 0);

  return {
    id: cart.id,
    items,
    count: items.filter((item) => {
      return item.quantity > 0;
    }).length,
    checkoutUrl: cart.checkoutUrl as string,
    discounts: getGroupedDiscountAllocations(
      [...cart.discountAllocations, ...lineDiscountAllocations] as CartDiscountAllocation[],
      subtotalAmount,
    ),
    discountCodes: cart.discountCodes.map(({ applicable, code }) => {
      return { code, applicable };
    }),
    selectedShippingOption: cartSelectedDeliveryOption
      ? {
          title: getSelectedShippingOptionTitle(cartSelectedDeliveryOption),
          cost: cartSelectedDeliveryOption.estimatedCost.amount as string,
          costAsString:
            parseInt(cartSelectedDeliveryOption.estimatedCost.amount as string) === 0
              ? FREE_SHIPPING_OPTION
              : formatAmountAsString(cartSelectedDeliveryOption.estimatedCost.amount as string),
        }
      : null,
    subscriptionDiscount,
    careplanDiscount,
    cost: {
      subtotal: {
        amount: subtotalAmount,
        amountAsString: formatAmountAsString(cart.cost.subtotalAmount.amount as string),
      },
      total: {
        amount: Number(cart.cost.totalAmount.amount as string),
        amountAsString: formatAmountAsString(cart.cost.totalAmount.amount as string),
      },
      checkoutChargeAmount: {
        amount: Number(cart.cost.checkoutChargeAmount.amount as string) + subscriptionDiscount,
        amountAsString: formatAmountAsString(
          `${Number(cart.cost.checkoutChargeAmount.amount as string) + subscriptionDiscount}`,
        ),
      },
    },
  };
};
